import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

interface CreditsContextType {
  credits: number;
  loading: boolean;
  refetch: () => Promise<void>;
  isSupreme: boolean;
  supremeUntil: Date | null;
  supremeDaysRemaining: number | null;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

function computeSupremeState(supremeUntilRaw: string | null) {
  if (!supremeUntilRaw) return { isSupreme: false, supremeUntil: null, supremeDaysRemaining: null };
  const expirationDate = new Date(supremeUntilRaw);
  const now = new Date();
  const isSupreme = expirationDate > now;
  const diffMs = expirationDate.getTime() - now.getTime();
  const daysRemaining = isSupreme ? Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24))) : 0;
  return {
    isSupreme,
    supremeUntil: expirationDate,
    supremeDaysRemaining: isSupreme ? daysRemaining : null,
  };
}

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [credits, setCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isSupreme, setIsSupreme] = useState(false);
  const [supremeUntil, setSupremeUntil] = useState<Date | null>(null);
  const [supremeDaysRemaining, setSupremeDaysRemaining] = useState<number | null>(null);

  const applySupremeState = (raw: string | null) => {
    const state = computeSupremeState(raw);
    setIsSupreme(state.isSupreme);
    setSupremeUntil(state.supremeUntil);
    setSupremeDaysRemaining(state.supremeDaysRemaining);
  };

  const fetchCredits = async () => {
    if (!user) {
      setCredits(0);
      setIsSupreme(false);
      setSupremeUntil(null);
      setSupremeDaysRemaining(null);
      setLoading(false);
      return;
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .select('credits, supreme_until')
      .eq('id', user.id)
      .single();
    
    if (!error && data) {
      setCredits(data.credits);
      applySupremeState(data.supreme_until as string | null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCredits();
    
    if (!user) return;
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('credits-changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`
      }, (payload) => {
        const newData = payload.new as Record<string, unknown>;
        if (newData.credits !== undefined) {
          setCredits(newData.credits as number);
        }
        if (newData.supreme_until !== undefined) {
          applySupremeState(newData.supreme_until as string | null);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <CreditsContext.Provider value={{
      credits,
      loading,
      refetch: fetchCredits,
      isSupreme,
      supremeUntil,
      supremeDaysRemaining,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}
